import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default color config
const DEFAULT_COLORS = {
  primary: "#1B4D3E",
  primary_hover: "#163d32",
  accent: "#D4AF37",
  light_bg: "#E8F5E9",
  card_bg: "#F8FAF9",
  sidebar_bg: "#FFFFFF",
  text_primary: "#1B4D3E",
  text_secondary: "#64748b"
};

// XSS Protection: Validate JWT format before trusting stored tokens
// Note: localStorage is used for token storage. For enhanced security, consider migrating to
// httpOnly cookies with CSRF protection, which prevents XSS token theft entirely.
// Current mitigations: JWT format validation, token expiry checks, redirect URL sanitization.
const isValidJwtFormat = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
};

// XSS Protection: Check token expiry from JWT payload
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch { return true; }
};

// XSS Protection: Sanitize redirect URLs - only allow http/https
const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return null;
};

// Helper to fetch branding/color data with a given token
const fetchBrandingAndColors = async (authToken) => {
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };
  const results = { branding: null, colors: null };
  try {
    const [brandingRes, colorRes] = await Promise.all([
      axios.get(`${API_URL}/api/branding`, headers).catch(() => null),
      axios.get(`${API_URL}/api/color-config`, headers).catch(() => null)
    ]);
    if (brandingRes) results.branding = brandingRes.data;
    if (colorRes) results.colors = colorRes.data;
  } catch (err) { console.warn('Branding fetch failed:', err?.message); }
  return results;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem('bondmaster_token');
    if (!isValidJwtFormat(stored) || isTokenExpired(stored)) {
      localStorage.removeItem('bondmaster_token');
      return null;
    }
    return stored;
  });
  const [branding, setBranding] = useState({ partner_logo_url: null, partner_favicon_url: null, partner_web_url: null, partner_name: null });
  const [colorConfig, setColorConfig] = useState(DEFAULT_COLORS);

  // Memoize getAuthHeaders - stable reference that only changes when token changes
  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Mount-only effect: all references (API_URL, fetchBrandingAndColors, isValidJwtFormat, DEFAULT_COLORS) are module-level constants
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('bondmaster_token');
      if (!isValidJwtFormat(storedToken) || isTokenExpired(storedToken)) {
        localStorage.removeItem('bondmaster_token');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        setToken(storedToken);
        if (response.data.role === 'client' || response.data.role === 'partner' || response.data.role === 'subpartner') {
          const { branding: b, colors: c } = await fetchBrandingAndColors(storedToken);
          if (b) setBranding(b);
          if (c) setColorConfig({ ...DEFAULT_COLORS, ...c });
        }
      } catch {
        localStorage.removeItem('bondmaster_token');
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // All references (API_URL, fetchBrandingAndColors, isValidJwtFormat, DEFAULT_COLORS) are module-level constants; state setters are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const login = useCallback(async (username, password, allowedPartnerCode = null) => {
    const payload = { username, password };
    if (allowedPartnerCode) payload.allowed_partner_code = allowedPartnerCode;
    const response = await axios.post(`${API_URL}/api/auth/login`, payload);
    const { token: newToken, user: userData } = response.data;
    if (!isValidJwtFormat(newToken)) {
      throw new Error('Invalid token received from server');
    }
    localStorage.setItem('bondmaster_token', newToken);
    setToken(newToken);
    setUser(userData);
    if (userData.role === 'client' || userData.role === 'partner' || userData.role === 'subpartner') {
      const { branding: b, colors: c } = await fetchBrandingAndColors(newToken);
      if (b) setBranding(b);
      if (c) setColorConfig({ ...DEFAULT_COLORS, ...c });
    }
    return userData;
  }, []);

  const logout = useCallback((redirectUrl = null) => {
    const targetUrl = sanitizeUrl(redirectUrl) || sanitizeUrl(branding.partner_web_url);
    localStorage.removeItem('bondmaster_token');
    if (targetUrl) {
      window.location.href = targetUrl;
      return;
    }
    setToken(null);
    setUser(null);
    setBranding({ partner_logo_url: null, partner_favicon_url: null, partner_web_url: null, partner_name: null });
    setColorConfig(DEFAULT_COLORS);
  }, [branding.partner_web_url]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, getAuthHeaders, branding, colorConfig }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
